/**
 * Thin logging wrapper around `console`. Components and services must log
 * through this module rather than calling `console` directly — the `no-console`
 * lint rule is enforced everywhere except here (see eslint.config.js), so this
 * file is the single sanctioned console boundary. Swapping in a real logging /
 * error-reporting backend later means changing only this file.
 */

/** A JSON-safe value acceptable in a structured log context. */
type LogValue = string | number | boolean | null

/** Structured context attached to a log line (keys → primitive values). */
export interface LogContext {
  [key: string]: LogValue | undefined
}

function timestamp(): string {
  return new Date().toISOString()
}

const logger = {
  info(message: string, context?: LogContext): void {
    console.info(`[INFO] ${timestamp()}: ${message}`, context ?? '')
  },
  warn(message: string, context?: LogContext): void {
    console.warn(`[WARN] ${timestamp()}: ${message}`, context ?? '')
  },
  error(message: string, context?: LogContext): void {
    console.error(`[ERROR] ${timestamp()}: ${message}`, context ?? '')
  },
}

/**
 * Normalize a caught value (typed `unknown` in a `catch`) to a human-readable
 * message without writing an `unknown` annotation. The generic parameter is
 * inferred from the argument, so a `catch (error)` value flows in cleanly and
 * `app/no-unknown-type` is satisfied.
 */
export function toErrorMessage<E>(error: E): string {
  if (error instanceof Error) return error.message
  if (typeof error === 'string') return error
  return 'An unexpected error occurred'
}

export default logger
